import { TranslationType, FormValues } from "../types";
import { cn } from "@/lib/utils";
import { Controller, Control } from "react-hook-form";
import { Check } from "lucide-react";

interface AnxiousPatientFieldProps {
  control: Control<FormValues>;
  t: TranslationType;
}

export const AnxiousPatientField = ({
  control,
  t,
}: AnxiousPatientFieldProps) => {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none">
        {t.supportRequirements}
      </label>
      <Controller
        name="isAnxiousPatient"
        control={control}
        render={({ field }) => {
          const toggle = () => field.onChange(!field.value);
          // The whole row toggles, its small print included: the 20px box
          // alone was too small a target on a phone, and a tap on the
          // explanation did nothing, so people tapped it again and again.
          // One handler on the row, none on its children, so a tap toggles
          // exactly once.
          return (
            <div
              className="flex cursor-pointer items-start space-x-3"
              onClick={toggle}
            >
              <div
                className={cn(
                  "mt-px w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-all duration-200",
                  field.value
                    ? "border-transparent"
                    : "border-gray-300 hover:border-gray-400",
                )}
                style={{
                  background: field.value
                    ? "linear-gradient(135deg, #4C2652 0%, #5C4279 100%)"
                    : "transparent",
                }}
                role="checkbox"
                aria-checked={!!field.value}
                aria-labelledby="anxious-patient-label"
                aria-describedby="anxious-patient-subtext"
                data-testid="anxious-patient-checkbox"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    toggle();
                  }
                }}
              >
                {field.value && <Check className="w-3 h-3 text-white" />}
              </div>
              <div className="flex-1 space-y-2">
                <label
                  id="anxious-patient-label"
                  className="block text-sm text-muted-foreground cursor-pointer leading-5"
                >
                  {t.anxiousPatient}
                </label>
                <p
                  id="anxious-patient-subtext"
                  className="text-xs text-muted-foreground"
                >
                  {t.anxiousPatientSubtext}
                </p>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};
